import type {
  BannerClaimIn,
  BannerDeeplinkIn,
  BannerDetailsIn,
  BannerIn,
  BannerTimerIn,
  LocalizedTextIn,
  LocalizedTextMap,
} from "../types";
import featureFlagService from "../../../../services/FeatureFlagService";

export const BANNER_DEEPLINK_VALIDATION_FIELDS: Record<
  BannerDeeplinkIn["screen"],
  readonly (keyof BannerDeeplinkIn)[]
> = {
  sportsbook_league: ["tournament_id"],
  sportsbook: [],
  sportsbook_match: ["event_id"],
  casino: [],
  casino_game: ["game_id"],
  casino_provider: ["provider_id", "eid"],
  casino_collection: ["eid"],
  casino_category: ["eid"],
  deposit: ["money_type"],
  user_profile: [],
  poker_table: [],
  poker_lobby: [],
  game_pass: [],
};

const pruneTextMap = (
  map?: LocalizedTextMap | null,
): LocalizedTextMap | undefined => {
  const entries = Object.entries(map ?? {}).filter(
    ([, value]) => value.trim() !== "",
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
};

const pruneColorList = (
  colors?: string[] | null,
): string[] | undefined => {
  const filtered = (colors ?? [])
    .map((color) => color.trim())
    .filter(Boolean);
  return filtered.length ? filtered : undefined;
};

const pruneString = (value?: string | null): string | undefined => {
  if (!value?.trim()) return undefined;
  return value.trim();
};

export const pruneBannerDeeplink = (
  deeplink?: BannerIn["deeplink"],
): BannerDeeplinkIn | undefined => {
  if (!deeplink || typeof deeplink === "string") return undefined;

  const tournamentId = pruneString(deeplink.tournament_id);
  const sportId = pruneString(deeplink.sport_id);
  const eventId = pruneString(deeplink.event_id);
  const gameId = pruneString(deeplink.game_id);
  const providerId = pruneString(deeplink.provider_id);
  const eid = pruneString(deeplink.eid);
  const code = pruneString(deeplink.code);

  switch (deeplink.screen) {
    case "sportsbook_league":
      return {
        screen: deeplink.screen,
        ...(tournamentId ? { tournament_id: tournamentId } : {}),
        ...(sportId ? { sport_id: sportId } : {}),
      };
    case "sportsbook_match":
      return {
        screen: deeplink.screen,
        ...(eventId ? { event_id: eventId } : {}),
      };
    case "deposit":
      return {
        screen: deeplink.screen,
        ...(deeplink.money_type ? { money_type: deeplink.money_type } : {}),
      };
    case "casino_game": {
      const includeProviderId =
        featureFlagService.isEnabled("casinoGameDeeplinkProvider");
      return {
        screen: deeplink.screen,
        ...(gameId ? { game_id: gameId } : {}),
        ...(includeProviderId && providerId ? { provider_id: providerId } : {}),
      };
    }
    case "poker_table":
      return {
        screen: deeplink.screen,
        ...(gameId ? { game_id: gameId } : {}),
      };
    case "casino_provider":
      return {
        screen: deeplink.screen,
        ...(providerId ? { provider_id: providerId } : {}),
        ...(eid ? { eid } : {}),
        ...(code ? { code } : {}),
      };
    case "casino_collection":
    case "casino_category":
      return {
        screen: deeplink.screen,
        ...(eid ? { eid } : {}),
        ...(code ? { code } : {}),
      };
    case "sportsbook":
    case "casino":
    case "user_profile":
    case "poker_lobby":
    case "game_pass":
      return { screen: deeplink.screen };
    default:
      return undefined;
  }
};

const pruneLocalizedText = (
  block?: LocalizedTextIn | null,
): LocalizedTextIn | undefined => {
  const text = pruneTextMap(block?.text);
  if (!text) return undefined;
  const color = pruneString(block?.color);

  return {
    text,
    ...(color ? { color } : {}),
    ...(block?.shadow ? { shadow: true } : {}),
  };
};

const pruneDetails = (
  details?: BannerDetailsIn | null,
): BannerDetailsIn | undefined => {
  if (!details) return undefined;

  const text = pruneTextMap(details.text);
  const color = pruneString(details.color);
  if (details.visible === undefined && !text && !color && !details.shadow) {
    return undefined;
  }

  return {
    ...(details.visible !== undefined ? { visible: details.visible } : {}),
    ...(text ? { text } : {}),
    ...(color ? { color } : {}),
    ...(details.shadow ? { shadow: true } : {}),
  };
};

const pruneTimer = (
  timer?: BannerTimerIn | null,
): BannerTimerIn | undefined => {
  if (!timer) return undefined;

  const backgroundColor = pruneString(timer.background_color);
  const textColor = pruneString(timer.text_color);
  const iconColor = pruneString(timer.icon_color);
  const borderColor = pruneString(timer.border_color);
  const borderWidth = timer.border_width;
  const hasContent =
    timer.visible !== undefined ||
    Boolean(backgroundColor || textColor || iconColor || borderColor) ||
    borderWidth != null ||
    Boolean(timer.shadow);
  if (!hasContent) return undefined;

  return {
    ...(timer.visible !== undefined ? { visible: timer.visible } : {}),
    ...(backgroundColor ? { background_color: backgroundColor } : {}),
    ...(textColor ? { text_color: textColor } : {}),
    ...(iconColor ? { icon_color: iconColor } : {}),
    ...(borderColor ? { border_color: borderColor } : {}),
    ...(borderWidth != null ? { border_width: borderWidth } : {}),
    ...(timer.shadow ? { shadow: true } : {}),
  };
};

const pruneClaim = (
  claim?: BannerClaimIn | null,
): BannerClaimIn | undefined => {
  if (!claim) return undefined;

  const text = pruneTextMap(claim.text);
  const textColor = pruneString(claim.text_color);
  const backgroundColor = pruneColorList(claim.background_color);
  const borderColor = pruneString(claim.border_color);
  const borderWidth = claim.border_width;
  const hasContent =
    claim.visible !== undefined ||
    Boolean(text || textColor || backgroundColor || borderColor) ||
    borderWidth != null ||
    Boolean(claim.shadow);
  if (!hasContent) return undefined;

  return {
    visible: claim.visible ?? false,
    ...(text ? { text } : {}),
    ...(textColor ? { text_color: textColor } : {}),
    ...(backgroundColor ? { background_color: backgroundColor } : {}),
    ...(borderColor ? { border_color: borderColor } : {}),
    ...(borderWidth != null ? { border_width: borderWidth } : {}),
    ...(claim.shadow ? { shadow: true } : {}),
  };
};

export const pruneBanner = (
  banner?: BannerIn | null,
): BannerIn | undefined => {
  if (!banner) return undefined;

  const backgroundColor = pruneColorList(banner.background_color);
  const borderColor = pruneColorList(banner.border_color);
  const deeplink = pruneBannerDeeplink(banner.deeplink);
  const title = pruneLocalizedText(banner.title);
  const shortDescription = pruneLocalizedText(banner.short_description);
  const longDescription = pruneLocalizedText(banner.long_description);
  const details = pruneDetails(banner.details);
  const timer = pruneTimer(banner.timer);
  const claim = pruneClaim(banner.claim);
  const imageUrl = pruneString(banner.image?.url);

  const result: BannerIn = {
    ...(backgroundColor ? { background_color: backgroundColor } : {}),
    ...(borderColor ? { border_color: borderColor } : {}),
    ...(deeplink ? { deeplink } : {}),
    ...(title ? { title } : {}),
    ...(shortDescription ? { short_description: shortDescription } : {}),
    ...(longDescription ? { long_description: longDescription } : {}),
    ...(details ? { details } : {}),
    ...(timer ? { timer } : {}),
    ...(claim ? { claim } : {}),
    ...(imageUrl ? { image: { url: imageUrl } } : {}),
  };

  return Object.keys(result).length ? result : undefined;
};
